import { TimeZoneType } from "type/time-zone";
import { TextMessageBusinessType, TextMessageTaxIdType } from "type/text-message-tax-id";
import { isValidPhone, isValidTaxId } from "shared/texting-fields";

export type SetupStepKey = "area-code" | "tax-id" | "mobile-verification" | "additional-settings" | "terms";

/** The wizard's path: a client with a tax ID registers a standard brand, which has no mobile verification. */
export const setupStepKeys = (hasTaxId: boolean): SetupStepKey[] => [
	"area-code",
	"tax-id",
	...(hasTaxId ? [] : ["mobile-verification" as const]),
	"additional-settings",
	"terms",
];

export interface SetupForm {
	hasTaxId: boolean;
	taxIdType: TextMessageTaxIdType;
	taxId: string;
	businessType: TextMessageBusinessType | "";
	legalBusinessName: string;
	mobile: string;
	callForwarding: string;
	timeZone: string;
	phoneRequired: boolean;
	agreeToTerms: boolean;
}

export const initialSetupForm = (taxIdType: TextMessageTaxIdType): SetupForm => ({
	hasTaxId: false,
	taxIdType,
	taxId: "",
	businessType: "",
	legalBusinessName: "",
	mobile: "",
	callForwarding: "",
	timeZone: "",
	phoneRequired: true,
	agreeToTerms: false,
});

/** Yes → No clears what the Yes answer collected, with no confirmation in the wizard. */
export const answerTaxId = (form: SetupForm, hasTaxId: boolean, defaultType: TextMessageTaxIdType): SetupForm => {
	if (hasTaxId || !form.hasTaxId) {
		return { ...form, hasTaxId };
	}
	return { ...form, hasTaxId, taxIdType: defaultType, taxId: "", businessType: "", legalBusinessName: "" };
};

export type SetupErrors = Partial<Record<"taxId" | "businessType" | "legalBusinessName" | "mobile" | "callForwarding" | "timeZone" | "agreeToTerms", string>>;

export const PHONE_ERROR = "Please enter a valid phone number.";
export const TAX_ID_ERROR = "Please enter a valid EIN (XX-XXXXXXX) or Canadian BN (9 digits).";
export const BUSINESS_TYPE_ERROR = "Please select a business type.";
export const LEGAL_NAME_ERROR = "Please enter your legal business name.";
export const TIME_ZONE_ERROR = "Please select a time zone.";
export const TERMS_ERROR = "Please agree to the Texting Terms of Service to finish setup.";

const clean = (errors: SetupErrors): SetupErrors =>
	Object.fromEntries(Object.entries(errors).filter(([, value]) => !!value)) as SetupErrors;

export const stepErrors = (key: SetupStepKey, form: SetupForm): SetupErrors => {
	switch(key) {
		case "tax-id":
			return form.hasTaxId
				? clean({
					taxId: isValidTaxId(form.taxId) ? undefined : TAX_ID_ERROR,
					businessType: form.businessType ? undefined : BUSINESS_TYPE_ERROR,
					legalBusinessName: form.legalBusinessName.trim() ? undefined : LEGAL_NAME_ERROR,
					mobile: isValidPhone(form.mobile) ? undefined : PHONE_ERROR,
				})
				: {};
		case "mobile-verification":
			return clean({ mobile: isValidPhone(form.mobile) ? undefined : PHONE_ERROR });
		case "additional-settings":
			return clean({
				callForwarding: isValidPhone(form.callForwarding) ? undefined : PHONE_ERROR,
				timeZone: TimeZoneType.getById(form.timeZone) ? undefined : TIME_ZONE_ERROR,
			});
		case "terms":
			return clean({ agreeToTerms: form.agreeToTerms ? undefined : TERMS_ERROR });
		default:
			return {};
	}
};
